'use strict'

// Promoted AI-agent card for the home page's Zone-3 row. Replaces the
// flat ◆-glyph AgentCard with a richer treatment per the brief:
//   • per-agent identity — colored tile + initial letter (no more
//     identical diamond placeholders)
//   • live status line — short "what they're doing right now" caption
//     with a coloured dot (`status` + `statusColor`)
//   • role chip kept next to the name
//   • clickable surface routes to /team-and-agents; the section-level
//     "Talk to an agent" CTA in main.js handles the conversation entry
//
// Per-card state (fed by main.js's `_homeAgents` via childrenAs:'state'):
//   { name, role?, color?, status?, statusColor?, caption?, placeholder? }
//   - color:       design-system color name for the identity tile (and
//                  the per-agent accent). Falsy → 'blue' fallback.
//   - status:      live status line ("3 tickets in flight"); falsy →
//                  the static `caption` is used as a quieter fallback.
//   - statusColor: dot color (e.g. 'green' for available, 'orange' for
//                  busy, 'blue' for active-on-something).
//   - placeholder: trailing "More agents coming soon" tile — renders a
//                  muted '+' instead of an identity letter, hides the
//                  status row.
export const AgentHeroCard = {
  tag: 'button',
  type: 'button',
  flow: 'y',
  gap: 'A',
  round: 'radiusSheet',
  padding: 'C',
  textAlign: 'left',
  cursor: 'pointer',
  color: 'title',
  background: 'overlay',
  border: 'none',
  transition: '160ms defaultBezier background, 160ms defaultBezier transform',
  ':hover': { theme: 'dialog', transform: 'translateY(-2px)' },

  onClick: (e, el) => el.router('/team-and-agents', el.getRoot()),

  // Identity tile — the agent's accent color filled with their initial
  // letter. Replaces the previous "everyone gets a ◆" glyph so the four
  // agents read as distinct characters at a glance.
  Glyph: {
    width: 'D',
    height: 'D',
    minWidth: 'D',
    round: 'radiusCard',
    align: 'center center',
    fontSize: 'C',
    fontWeight: '700',
    // The placeholder tile sits on the adaptive 'overlay' wash and takes the
    // adaptive `title` ink. A real agent sits on an opaque hue that comes from
    // DATA (s.color — 'blue' / 'purple' / 'green' / 'orange' from _homeAgents).
    // Fixed white read 3.62 on blue and 2.81 on green; fixed near-black would
    // read 3.0 on purple. `inkOnPlate` measures the plate and picks.
    color: (el, s) => (s.placeholder ? 'title' : el.call('inkOnPlate', s.color || 'blue')),
    background: (el, s) => (s.placeholder ? 'overlay' : s.color || 'blue'),
    text: (el, s) => (s.placeholder ? '+' : (s.name || '?').trim().charAt(0).toUpperCase())
  },

  Head: {
    flow: 'x',
    gap: 'Z',
    align: 'center',
    flexWrap: 'wrap',

    Name: {
      tag: 'span',
      fontSize: 'B',
      fontWeight: '700',
      color: 'title',
      text: (el, s) => s.name
    },

    Role: {
      hide: (el, s) => !s.role,
      tag: 'span',
      fontSize: 'Y2',
      fontWeight: '700',
      padding: 'V Z',
      round: 'X',
      color: 'caption',
      background: 'overlay',
      text: (el, s) => s.role
    }
  },

  // Live status row — small dot + short caption. Placeholder cards
  // (the trailing "More agents coming soon") hide the dot but still
  // render the caption.
  Status: {
    flow: 'x',
    gap: 'Z',
    align: 'center',
    minHeight: 'A',

    Dot: {
      hide: (el, s) => s.placeholder || !s.status,
      width: 'Y',
      height: 'Y',
      minWidth: 'Y',
      round: '50%',
      background: (el, s) => s.statusColor || 'green'
    },

    Text: {
      tag: 'span',
      fontSize: 'Y',
      lineHeight: '1.4',
      color: 'caption',
      text: (el, s) => s.status || s.caption || ''
    }
  }
}
